import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CONCURRENTEN_BRABANT, SPULA_POSTCODE } from '@/constants/laadplein-data';
import type { Concurrent } from '@/types/laadplein';

type DbConcurrent = {
  id: string;
  naam: string;
  adres: string | null;
  aantal_truck_laders: number | null;
  aantal_auto_laders: number | null;
  max_vermogen_kw: number | null;
  operator: string | null;
  operationeel: boolean | null;
  locatie_id: string | null;
  latitude: number | null;
  longitude: number | null;
  ocm_id: string | null;
};

function mapDbRecord(row: DbConcurrent): Concurrent {
  const adresParts = (row.adres || '').split(',');
  const plaats = adresParts[adresParts.length - 1].trim() || '';

  const truckLaders = row.aantal_truck_laders ?? 0;
  const autoLaders = row.aantal_auto_laders ?? 0;

  return {
    id: row.id,
    naam: row.naam,
    plaats,
    type: truckLaders > 0 ? 'Truck HPC' : 'HPC',
    punten: truckLaders + autoLaders,
    kw: row.max_vermogen_kw ?? 0,
    operator: row.operator ?? '',
    status: row.operationeel ? 'operationeel' : 'niet operationeel',
    latitude: row.latitude,
    longitude: row.longitude,
    truckLaders,
    autoLaders,
    ocmId: row.ocm_id,
  };
}

type RefreshParams = {
  latitude: number;
  longitude: number;
  radius_km?: number;
};

export function useLaadpleinConcurrenten(locatieId?: string) {
  const queryClient = useQueryClient();

  const queryKey = ['laadplein-concurrenten', locatieId ?? 'all'];

  const { data: dbRecords, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = (supabase as any)
        .from('laadplein_concurrenten')
        .select('id, naam, adres, aantal_truck_laders, aantal_auto_laders, max_vermogen_kw, operator, operationeel, locatie_id, latitude, longitude, ocm_id');

      if (locatieId) {
        query = query.eq('locatie_id', locatieId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as DbConcurrent[];
    },
  });

  const isLive = !isLoading && (dbRecords?.length ?? 0) > 0;

  const concurrenten: Concurrent[] = isLive
    ? dbRecords!.map(mapDbRecord)
    : CONCURRENTEN_BRABANT;

  const refresh = useMutation({
    mutationFn: async ({ latitude, longitude, radius_km = 25 }: RefreshParams) => {
      const { data, error } = await supabase.functions.invoke('laadplein-ocm-competitors', {
        body: { latitude, longitude, radius_km },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Load all NL chargers
  const refreshNL = useMutation({
    mutationFn: async (opts?: { min_power_kw?: number; max_results?: number }) => {
      const { data, error } = await supabase.functions.invoke('laadplein-ocm-competitors', {
        body: { country_nl: true, ...opts },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    concurrenten,
    isLive,
    isLoading,
    refresh,
    refreshNL,
    spulaPostcode: SPULA_POSTCODE,
  };
}
